import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, MapPin, Shield, Award, PoundSterling, Send, CheckCircle, Star, Clock, Lock } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    postcode: "",
    service_type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.service_type || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thanks! I'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", postcode: "", service_type: "", message: "" });
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again or give me a call.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Get a Quote | Contact Expert Carpenter | Greater Manchester</title>
        <meta name="description" content="Get a free quote for door hanging, skirting boards, architraves or wall panelling in Greater Manchester. Call 07300 405045 or fill in the form. 12-month guarantee, no VAT." />
        <link rel="canonical" href="https://expert-carpenter.co.uk/contact" />
      </Helmet>
      
      {/* Hero */}
      <section className="py-10 md:py-20 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C9A227] font-semibold tracking-wider uppercase mb-3 text-sm md:text-base">Get In Touch</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Get a Free Quote
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Tell me about your job and I'll get back to you with a quote. 
            Simple jobs can often be quoted over the phone or from photos.
          </p>
          
          <a href="tel:07300405045" className="inline-flex items-center justify-center gap-3 mt-6 bg-[#C9A227] text-black font-bold py-4 px-8 rounded-lg text-lg md:hidden min-h-[56px]">
            <Phone className="h-6 w-6" />
            Call 07300 405045
          </a>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-10 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
            
            {/* Contact Form */}
            <div className="lg:col-span-3 order-1 lg:order-2">
              <form onSubmit={handleSubmit} className="bg-[#f8f7f5] p-6 md:p-10 rounded-2xl shadow-lg" data-testid="contact-form">
                <h3 className="text-2xl md:text-2xl font-bold text-[#3A3A3A] mb-6">Tell Me About Your Job</h3>
                <div className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-base font-semibold text-gray-700 mb-2">
                        Name *
                      </label>
                      <Input
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="h-14 bg-white text-base"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-base font-semibold text-gray-700 mb-2">
                        Phone *
                      </label>
                      <Input
                        placeholder="Your phone number"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="h-14 bg-white text-base"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-base font-semibold text-gray-700 mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        placeholder="Your email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="h-14 bg-white text-base"
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <label className="block text-base font-semibold text-gray-700 mb-2">
                        Postcode
                      </label>
                      <Input
                        placeholder="e.g. SK8 1AB"
                        value={formData.postcode}
                        onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                        className="h-14 bg-white text-base"
                        data-testid="contact-postcode-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-base font-semibold text-gray-700 mb-2">
                      What do you need? *
                    </label>
                    <Select
                      value={formData.service_type}
                      onValueChange={(value) =>
                        setFormData({ ...formData, service_type: value })
                      }
                    >
                      <SelectTrigger className="h-14 bg-white text-base" data-testid="contact-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="doors" className="text-base py-3">Door Hanging / Fitting</SelectItem>
                        <SelectItem value="trimwork" className="text-base py-3">Skirting & Architraves</SelectItem>
                        <SelectItem value="panelling" className="text-base py-3">Wall Panelling</SelectItem>
                        <SelectItem value="sanding" className="text-base py-3">Floor Sanding</SelectItem>
                        <SelectItem value="multiple" className="text-base py-3">Multiple Jobs</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-base font-semibold text-gray-700 mb-2">
                      Job Details *
                    </label>
                    <Textarea
                      placeholder="Tell me about the job - how many doors, which rooms, any specifics..."
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      rows={5}
                      required
                      className="bg-white text-base min-h-[140px]"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-16 bg-[#2D5A4A] hover:bg-[#234839] text-lg font-bold"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? "Sending..." : (
                      <>
                        Send Enquiry
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>

                  {/* Trust signals under submit */}
                  <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-4 w-4 text-[#2D5A4A]" />
                      Quick response
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Lock className="h-4 w-4 text-[#2D5A4A]" />
                      No spam, ever
                    </span>
                    <span className="flex items-center gap-1.5">
                      <CheckCircle className="h-4 w-4 text-[#2D5A4A]" />
                      Free quote
                    </span>
                  </div>
                </div>
              </form>
              
              {/* Mini Testimonial */}
              <div className="mt-6 bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#2D5A4A] rounded-full flex items-center justify-center text-white font-bold text-lg">
                    S
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-[#C9A227] text-[#C9A227]" />
                      ))}
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      "Quick response to my enquiry, fair quote, and did a great job on the doors. Would definitely recommend."
                    </p>
                    <p className="text-gray-500 text-sm mt-2 font-medium">— Sarah M., Stockport</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="lg:col-span-2 order-2 lg:order-1">
              <h2 className="text-2xl font-bold text-[#3A3A3A] mb-6">
                Contact Details
              </h2>

              <div className="space-y-4 mb-8">
                <a href="tel:07300405045" className="flex items-center gap-4 group p-5 rounded-xl bg-[#2D5A4A] hover:bg-[#234839] transition-colors min-h-[80px]" data-testid="contact-call-btn">
                  <div className="p-3 bg-[#C9A227] rounded-lg">
                    <Phone className="h-7 w-7 text-black" />
                  </div>
                  <div>
                    <p className="text-sm text-white/70">Call or text</p>
                    <p className="text-2xl font-bold text-white">07300 405045</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-5 rounded-xl bg-[#f8f7f5]">
                  <div className="p-3 bg-[#2D5A4A] rounded-lg">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Based in</p>
                    <p className="text-xl font-bold text-[#3A3A3A]">
                      Greater Manchester
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-5 bg-[#f8f7f5] rounded-xl mb-8">
                <h3 className="font-bold text-[#3A3A3A] mb-3 text-lg">Areas Covered</h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  Greater Manchester, Stockport, Cheadle, Bramhall, Poynton, Macclesfield,
                  Wilmslow, Marple, and surrounding areas.
                </p>
              </div>

              {/* What You Get */}
              <div className="space-y-3">
                <h3 className="font-bold text-[#3A3A3A] mb-4 text-lg">What You Get</h3>
                <div className="flex items-center gap-4 p-4 rounded-lg bg-[#2D5A4A]/5">
                  <Shield className="h-6 w-6 text-[#2D5A4A] flex-shrink-0" />
                  <span className="text-gray-700 font-medium text-base">12-month guarantee on all work</span>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-lg bg-[#2D5A4A]/5">
                  <Award className="h-6 w-6 text-[#2D5A4A] flex-shrink-0" />
                  <span className="text-gray-700 font-medium text-base">I'll beat any like-for-like quote by 10%</span>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-lg bg-[#2D5A4A]/5">
                  <PoundSterling className="h-6 w-6 text-[#2D5A4A] flex-shrink-0" />
                  <span className="text-gray-700 font-medium text-base">No VAT - price quoted is what you pay</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
